import type { MouseEvent } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { courseTypesLabels } from '@/constants/catalog/course';
import {
  courseTypeIconNames,
  resourceIconMap,
} from '@/constants/resources/resources-icons';
import type { EmbeddedCourse } from '@/models/resources';
import { formatDurationWithModality } from '@/utils/format-duration-with-modality';

interface SelectedCourseCardProps {
  course: EmbeddedCourse;
  onRemove?: () => void;
  removable?: boolean;
}

interface InstitutionLogoProps {
  course: EmbeddedCourse;
  size?: number;
}

interface CourseMetadataProps {
  course: EmbeddedCourse;
  variant?: 'inline' | 'stacked';
}

const InstitutionLogo = ({ course, size = 24 }: InstitutionLogoProps) => (
  <Box
    display="flex"
    alignItems="center"
    sx={{
      backgroundColor: course.external_institution_background_color,
      borderRadius: 0.5,
      width: size,
      height: size,
    }}
  >
    {course.external_institution_logo_url && (
      <Box
        component="img"
        src={course.external_institution_logo_url}
        alt={`${course.institution_name} logo`}
        sx={{ objectFit: 'contain', objectPosition: 'center', width: '100%' }}
      />
    )}
  </Box>
);

const CourseMetadata = ({
  course,
  variant = 'inline',
}: CourseMetadataProps) => {
  const icon = courseTypeIconNames[course.course_type];
  const Icon = icon ? resourceIconMap[icon] : null;
  const iconSx = { fontSize: 16 };
  const duration = formatDurationWithModality(
    course?.duration,
    course?.duration_type,
    course?.modality
  );

  if (variant === 'stacked') {
    return (
      <Stack gap={0.5}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {Icon && <Icon sx={iconSx} />}
          <Typography variant="body2">
            {courseTypesLabels[course.course_type]}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <AccessTimeIcon sx={iconSx} />
          <Typography variant="body2">{duration}</Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      {Icon && <Icon sx={iconSx} />}
      <Typography variant="body2">
        {courseTypesLabels[course.course_type]} •
      </Typography>
      <AccessTimeIcon sx={iconSx} />
      <Typography variant="body2">{duration}</Typography>
    </Stack>
  );
};

const SimpleView = ({ course }: { course: EmbeddedCourse }) => (
  <Box>
    <Stack direction="row" alignItems="center" gap={1} mb={1}>
      <InstitutionLogo course={course} />
      <Typography variant="h4" color="neutral.900" flex={1}>
        {course.name}
      </Typography>
    </Stack>
    <CourseMetadata course={course} />
  </Box>
);

const RemovableView = ({
  course,
  onRemove,
}: {
  course: EmbeddedCourse;
  onRemove?: () => void;
}) => {
  const handleRemoveClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove?.();
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={2}
      width="100%"
      p={2}
      border="1px solid"
      borderColor="neutral.200"
      borderRadius={1}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <InstitutionLogo course={course} size={72} />
        <Stack gap={0.5} alignItems="flex-start" flex={1}>
          <Typography variant="h3" flex={1}>
            {course.name}
          </Typography>
          <CourseMetadata course={course} variant="stacked" />
        </Stack>
      </Box>
      <IconButton
        onClick={handleRemoveClick}
        aria-label="Remover curso"
        sx={{ color: 'neutral.500' }}
      >
        <DeleteIcon fontSize="small" sx={{ color: 'primary.dark' }} />
      </IconButton>
    </Stack>
  );
};

export const SelectedCourseCard = ({
  course,
  onRemove,
  removable,
}: SelectedCourseCardProps) => {
  if (removable) {
    return <RemovableView course={course} onRemove={onRemove} />;
  }

  return <SimpleView course={course} />;
};
